import React, { useEffect, useMemo, useState } from 'react';
import {
    CCardList,
    DateRange,
    DBlock,
    DDateRangePicker,
    DIdAutocomplete,
    useCardListSort,
    usePaginator,
} from '../../../../../dum';
import {
    ColumnOrder,
    OrderRequestsPageQuery,
    OrderRequestsSortableFields,
    RoleId,
    useCurrentUserQuery,
    useOrderRequestsPageLazyQuery,
} from '../../../../../../services';
import {
    SDeleteOrderRequestDialog,
    SUpdateOrderRequestDetailsDialog,
    SUpdateOrderRequestStatusDialog,
    SUpsertOrderRequestDialog,
} from '../../../../../smart';
import {
    COLUMN_LABELS,
    DATE_FORMAT,
    shortLabel,
    useDebounce,
    useNumber,
    useString,
} from '../../../../../../helpers';
import dayjs from 'dayjs';
import { OrderRequestCard } from './order-request-card';

type OrderRequestDoc =
    OrderRequestsPageQuery['paginatedOrderRequests']['docs'][number];

export function OrderRequestsListBlock() {
    const [query, { data, previousData, loading }] =
        useOrderRequestsPageLazyQuery();
    const [upsertOpen, setUpsertOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [statusOpen, setStatusOpen] = useState(false);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [orderRequestId, setOrderRequestId] = useState<null | number>(null);
    const [statusId, setStatusId] = useState<null | number>(null);

    // Status changes are admin-only (Super / General), matching the backend
    // updateOrderRequestStatus gate. Non-admins never see the context item.
    const { data: currentUserData } = useCurrentUserQuery();
    const isAdmin =
        !!currentUserData &&
        [RoleId.Super, RoleId.Admin].some((roleId) =>
            currentUserData.currentUser.role_ids.includes(roleId),
        );
    // Editing notes / entrega estimada is for sales users (matching the backend
    // updateOrderRequestDetails gate, RoleId.Sales). Asistente Ventas is
    // read-only, so it's deliberately excluded — same rule as the sales list.
    const isSales =
        !!currentUserData &&
        [RoleId.Super, RoleId.Admin, RoleId.Sales].some((roleId) =>
            currentUserData.currentUser.role_ids.includes(roleId),
        );

    // Typed sort: chips and query share the generated enum (no index mapping).
    const { sort, toggle: toggleSort } =
        useCardListSort<OrderRequestsSortableFields>(
            OrderRequestsListBlock.name,
        );

    const [orderRequestStatusId, setOrderRequestStatusId] = useNumber({
        KEY: OrderRequestsListBlock.name + '_OrderRequestStatusId',
    });

    const [accountId, setAccountId] = useNumber({
        KEY: OrderRequestsListBlock.name + '_AccountId',
    });

    const [filter, setFilter] = useString({
        KEY: OrderRequestsListBlock.name + '_Filter',
    });

    const debouncedFilter = useDebounce(filter, 400);

    const [startDate, setStartDate] = useString({
        KEY: OrderRequestsListBlock.name + '_StartDate',
    });

    const [endDate, setEndDate] = useString({
        KEY: OrderRequestsListBlock.name + '_EndDate',
    });

    const { take, setSkip, skip } = usePaginator(
        {
            KEY: OrderRequestsListBlock.name + '_Paginator',
        },
        [startDate, endDate, debouncedFilter, accountId, orderRequestStatusId],
    );

    useEffect(() => {
        void query({
            variables: {
                skip,
                take,
                startDate: startDate || null,
                endDate: endDate || null,
                accountId,
                filter: debouncedFilter,
                sortField: sort?.field ?? null,
                sortOrder: sort
                    ? sort.order === 'asc'
                        ? ColumnOrder.Asc
                        : ColumnOrder.Desc
                    : null,
                orderRequestStatusId,
            },
        });
    }, [
        query, skip, take, startDate, endDate, debouncedFilter,
        sort, accountId, orderRequestStatusId,
    ]);

    const count =
        data && data?.paginatedOrderRequests.count >= 0
            ? data?.paginatedOrderRequests.count
            : previousData?.paginatedOrderRequests.count;

    const orderRequests: OrderRequestsPageQuery['paginatedOrderRequests']['docs'] =
        data?.paginatedOrderRequests.docs ||
        previousData?.paginatedOrderRequests.docs ||
        [];

    const handleEditOrderRequest = useMemo(() => {
        return (orderRequest: OrderRequestDoc) => {
            setOrderRequestId(orderRequest.id);
            setUpsertOpen(true);
        };
    }, []);

    const handleDeleteOrderRequest = useMemo(() => {
        return (orderRequest: OrderRequestDoc) => {
            setOrderRequestId(orderRequest.id);
            setDeleteOpen(true);
        };
    }, []);

    const handleChangeStatus = useMemo(() => {
        return (orderRequest: OrderRequestDoc) => {
            setOrderRequestId(orderRequest.id);
            setStatusId(orderRequest.order_request_status?.id ?? null);
            setStatusOpen(true);
        };
    }, []);

    const handleEditDetails = useMemo(() => {
        return (orderRequest: OrderRequestDoc) => {
            setOrderRequestId(orderRequest.id);
            setDetailsOpen(true);
        };
    }, []);

    return (
        <DBlock>
            <CCardList<OrderRequestDoc, OrderRequestsSortableFields>
                title={'Pedidos'}
                data={orderRequests}
                loading={loading}
                keyOf={(orderRequest) => orderRequest.id}
                renderItem={(orderRequest) => (
                    <OrderRequestCard
                        orderRequest={orderRequest}
                        onEdit={handleEditOrderRequest}
                        onDelete={handleDeleteOrderRequest}
                        onChangeStatus={
                            isAdmin ? handleChangeStatus : undefined
                        }
                        onEditDetails={
                            isSales ? handleEditDetails : undefined
                        }
                    />
                )}
                skip={skip}
                take={take}
                count={count}
                setSkip={setSkip}
                filterValue={filter}
                onFilterChange={setFilter}
                filterHelperText={'Folio del pedido, cliente, producto o notas.'}
                activeFilterCount={
                    (startDate && endDate ? 1 : 0) +
                    (orderRequestStatusId != null ? 1 : 0) +
                    (accountId != null ? 1 : 0)
                }
                filters={
                    <>
                        <DDateRangePicker
                            label={'Fecha'}
                            value={startDate && endDate ? [dayjs(startDate), dayjs(endDate)] : undefined}
                            onChange={(range: DateRange) => {
                                setStartDate(range[0].format(DATE_FORMAT));
                                setEndDate(range[1].format(DATE_FORMAT));
                            }}
                            onClear={() => { setStartDate(''); setEndDate(''); }}
                            disableGutters
                        />
                        <DIdAutocomplete
                            id={orderRequestStatusId}
                            onChange={setOrderRequestStatusId}
                            itemText={'name'}
                            label={'Estatus'}
                            options={data?.getOrderRequestStatuses || []}
                            disableGutters
                        />
                        <DIdAutocomplete
                            id={accountId}
                            onChange={setAccountId}
                            itemText={'name'}
                            label={'Cliente'}
                            options={data?.getAccounts || []}
                            disableGutters
                        />
                    </>
                }
                sortOptions={[
                    { label: 'Código', field: OrderRequestsSortableFields.OrderCode },
                    { label: 'Fecha', field: OrderRequestsSortableFields.Date },
                    { label: 'Entrega estimada', field: OrderRequestsSortableFields.EstimatedDeliveryDate },
                ]}
                sort={sort}
                onSortToggle={toggleSort}
                onItemDoubleClick={handleEditOrderRequest}
                contextItems={[
                    {
                        title: shortLabel(COLUMN_LABELS.edit),
                        disable: (orderRequest) => !orderRequest?.is_editable,
                        callback: (orderRequest) => handleEditOrderRequest(orderRequest),
                    },
                    {
                        title: shortLabel(COLUMN_LABELS.delete),
                        disable: (orderRequest) => !orderRequest?.is_deletable,
                        callback: (orderRequest) => handleDeleteOrderRequest(orderRequest),
                    },
                    ...(isAdmin
                        ? [
                              {
                                  title: shortLabel(
                                      COLUMN_LABELS.change_status,
                                  ),
                                  callback: (orderRequest: OrderRequestDoc) =>
                                      handleChangeStatus(orderRequest),
                              },
                          ]
                        : []),
                    ...(isSales
                        ? [
                              {
                                  title: shortLabel(
                                      COLUMN_LABELS.edit_optional_details,
                                  ),
                                  callback: (orderRequest: OrderRequestDoc) =>
                                      handleEditDetails(orderRequest),
                              },
                          ]
                        : []),
                ]}
            />
            <SUpsertOrderRequestDialog
                open={upsertOpen}
                setOpen={setUpsertOpen}
                orderRequestId={orderRequestId}
            />
            <SDeleteOrderRequestDialog
                open={deleteOpen}
                setOpen={setDeleteOpen}
                orderRequestId={orderRequestId}
            />
            <SUpdateOrderRequestStatusDialog
                open={statusOpen}
                setOpen={setStatusOpen}
                orderRequestId={orderRequestId}
                currentStatusId={statusId}
            />
            <SUpdateOrderRequestDetailsDialog
                open={detailsOpen}
                setOpen={setDetailsOpen}
                orderRequestId={orderRequestId}
            />
        </DBlock>
    );
}
